import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, MapPin, Clock, AlertCircle, ChevronDown } from "lucide-react";
import SectionHeading from "../components/SectionHeading";

/**
 * Team Calendar — July 8, 2026 → August 2026.
 * `events` uses ISO date strings (YYYY-MM-DD). Each day can have multiple events.
 */
const events = {
  "2026-07-08": [{ type: "off", title: "Off Day", note: "No practice / no meetings" }],
  "2026-07-09": [{ type: "off", title: "Off Day", note: "No practice / no meetings" }],
  "2026-07-10": [{
    type: "training",
    title: "Helmet Fitting + Speed & Agility",
    time: "6:00 – 7:30 PM",
    location: "621 Catalpa Park Way, San Jacinto CA 92582",
  }],
  "2026-07-11": [{
    type: "meeting",
    title: "Cheer Coaches Conference",
    time: "10:00 AM – 2:00 PM",
    location: "Azur Hotel, Ontario",
  }],
  "2026-07-12": [{
    type: "meeting",
    title: "Head Coaches Conference",
    time: "10:00 AM – 12:00 PM",
    location: "Azur Hotel, Ontario",
  }],
  "2026-07-14": [{
    type: "training",
    title: "Speed & Agility Training",
    time: "6:00 – 7:30 PM",
    location: "621 Catalpa Park Way, San Jacinto CA 92582",
  }],
  "2026-07-16": [{
    type: "training",
    title: "Speed & Agility Training",
    time: "6:00 – 7:30 PM",
    location: "621 Catalpa Park Way, San Jacinto CA 92582",
  }],
  "2026-07-20": [{ type: "practice", title: "Practice", time: "6:15 – 8:00 PM", location: "Rancho Viejo" }],
  "2026-07-21": [{ type: "practice", title: "Practice", time: "6:15 – 8:00 PM", location: "Rancho Viejo" }],
  "2026-07-22": [{ type: "practice", title: "Practice", time: "6:15 – 8:00 PM", location: "Rancho Viejo" }],
  "2026-07-23": [{ type: "practice", title: "Practice", time: "6:15 – 8:00 PM", location: "Rancho Viejo" }],
  "2026-07-24": [{ type: "practice", title: "Practice", time: "6:15 – 8:00 PM", location: "Rancho Viejo" }],

  // ---------- AUGUST 2026 ----------
  // 8/3 – 8/12 practices also include helmets + shoulder pads distribution
  "2026-08-03": [
    { type: "practice", title: "Practice", time: "6:15 – 8:00 PM", location: "Location TBD — announced before practice" },
    { type: "meeting", title: "Gear · Helmet + Shoulder Pad Distribution" },
  ],
  "2026-08-05": [
    { type: "practice", title: "Practice", time: "6:15 – 8:00 PM", location: "Location TBD — announced before practice" },
    { type: "meeting", title: "Gear · Helmet + Shoulder Pad Distribution" },
  ],
  "2026-08-06": [
    { type: "practice", title: "Practice", time: "6:15 – 8:00 PM", location: "Location TBD — announced before practice" },
    { type: "meeting", title: "Gear · Helmet + Shoulder Pad Distribution" },
  ],
  "2026-08-07": [
    { type: "practice", title: "Practice", time: "6:15 – 8:00 PM", location: "Location TBD — announced before practice" },
    { type: "meeting", title: "Gear · Helmet + Shoulder Pad Distribution" },
  ],
  "2026-08-10": [
    { type: "practice", title: "Practice", time: "6:15 – 8:00 PM", location: "Location TBD — announced before practice" },
    { type: "meeting", title: "Gear · Helmet + Shoulder Pad Distribution" },
  ],
  "2026-08-12": [
    { type: "practice", title: "Practice", time: "6:15 – 8:00 PM", location: "Location TBD — announced before practice" },
    { type: "meeting", title: "Gear · Helmet + Shoulder Pad Distribution" },
  ],
  // 8/14 onwards — practice only (M/W/F)
  "2026-08-14": [
    { type: "payment", title: "All Payments Due", note: "$100 football / $225 cheer — bring to practice or contact Damon Johnson" },
    { type: "practice", title: "Practice", time: "6:15 – 8:00 PM", location: "Location TBD — announced before practice" },
  ],
  "2026-08-17": [{ type: "practice", title: "Practice", time: "6:15 – 8:00 PM", location: "Location TBD — announced before practice" }],
  "2026-08-19": [{ type: "practice", title: "Practice", time: "6:15 – 8:00 PM", location: "Location TBD — announced before practice" }],
  "2026-08-21": [{ type: "practice", title: "Practice", time: "6:15 – 8:00 PM", location: "Location TBD — announced before practice" }],
  "2026-08-24": [{ type: "practice", title: "Practice", time: "6:15 – 8:00 PM", location: "Location TBD — announced before practice" }],
  "2026-08-26": [{ type: "practice", title: "Practice", time: "6:15 – 8:00 PM", location: "Location TBD — announced before practice" }],
  "2026-08-28": [{ type: "practice", title: "Practice", time: "6:15 – 8:00 PM", location: "Location TBD — announced before practice" }],
  "2026-08-31": [{ type: "practice", title: "Practice", time: "6:15 – 8:00 PM", location: "Location TBD — announced before practice" }],
};

const TYPE_STYLES = {
  practice:  { bg: "bg-[#2D5F3F]", text: "text-[#FDFBF7]", label: "Practice" },
  training:  { bg: "bg-[#0A0A0A]", text: "text-[#FDFBF7]", label: "Training" },
  meeting:   { bg: "bg-[#B8863F]", text: "text-[#FDFBF7]", label: "Meeting" },
  payment:   { bg: "bg-[#B91C1C]", text: "text-[#FDFBF7]", label: "Payment Due" },
  off:       { bg: "bg-black/25", text: "text-[#FDFBF7]", label: "Off" },
};

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAY_ABBR = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

function toISO(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function MonthGrid({ year, month }) {
  const first = new Date(year, month, 1);
  const startWeekday = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div data-testid={`month-${MONTH_NAMES[month].toLowerCase()}-${year}`} className="bg-white border border-black/15">
      <div className="bg-[#2D5F3F] text-[#FDFBF7] px-5 py-3 font-display uppercase tracking-[0.25em] text-sm font-black">
        {MONTH_NAMES[month]} {year}
      </div>
      <div className="grid grid-cols-7 border-b border-black/10 bg-[#F5EFE0]">
        {DAY_ABBR.map((d) => (
          <div key={d} className="text-center font-display uppercase text-[10px] tracking-[0.25em] font-bold text-[#2D5F3F] py-2">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((d, idx) => {
          if (d === null) return <div key={idx} className="min-h-[92px] md:min-h-[110px] border-r border-b border-black/5 bg-[#FAF6EC]/60" />;
          const iso = toISO(year, month, d);
          const dayEvents = events[iso] || [];
          return (
            <div
              key={idx}
              data-testid={`day-${iso}`}
              className={`min-h-[92px] md:min-h-[110px] border-r border-b border-black/10 p-1.5 md:p-2 flex flex-col ${
                dayEvents.length ? "bg-[#F5EFE0]" : "bg-white"
              }`}
            >
              <div className="flex items-baseline justify-between">
                <span className="font-display font-black text-lg md:text-xl text-[#0A0A0A]">{d}</span>
                {dayEvents.length > 0 && (
                  <span className="w-1.5 h-1.5 rounded-full bg-[#2D5F3F]" aria-hidden />
                )}
              </div>
              <div className="mt-1 flex flex-col gap-1">
                {dayEvents.map((e, i) => {
                  const style = TYPE_STYLES[e.type] || TYPE_STYLES.practice;
                  return (
                    <div
                      key={i}
                      className={`${style.bg} ${style.text} px-1.5 py-1 text-[10px] md:text-[11px] leading-tight font-bold uppercase tracking-wider truncate`}
                      title={`${e.title}${e.time ? " · " + e.time : ""}${e.location ? " · " + e.location : ""}`}
                    >
                      {e.title}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Calendar() {
  const [upcomingOpen, setUpcomingOpen] = useState(true);
  const [pastOpen, setPastOpen] = useState(false);

  // Split events into past / upcoming based on today
  const { upcoming, past } = useMemo(() => {
    const all = Object.entries(events)
      .sort(([a], [b]) => a.localeCompare(b))
      .flatMap(([iso, arr]) => arr.map((e) => ({ iso, ...e })));
    const today = new Date();
    const todayISO = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    return {
      upcoming: all.filter((e) => e.iso >= todayISO),
      past: all.filter((e) => e.iso < todayISO).reverse(), // newest past first
    };
  }, []);

  return (
    <div data-testid="calendar-page" className="bg-[#FDFBF7] py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <SectionHeading
          eyebrow="Team Calendar · Season Kick-off"
          title="July → August 2026"
          description="Practices, meetings, and off days. Bring your gear, be on time."
        />

        {/* Always-updating notice */}
        <div data-testid="calendar-notice" className="mb-10 flex items-start gap-4 bg-[#2D5F3F] text-[#FDFBF7] p-5 md:p-6 border-l-8 border-[#B8863F]">
          <AlertCircle className="w-6 h-6 shrink-0 mt-0.5" strokeWidth={2.5} />
          <div>
            <div className="font-display text-xs uppercase tracking-[0.3em] font-bold opacity-85">Heads up</div>
            <p className="mt-1 font-bold leading-snug">
              The calendar is updated regularly — nothing is set in stone. Please check back at least once a week.
              Follow-up details go out via email to registered families.
            </p>
          </div>
        </div>

        {/* Legend */}
        <div className="mb-8 flex flex-wrap items-center gap-3 text-xs font-display uppercase tracking-wider" data-testid="calendar-legend">
          {Object.entries(TYPE_STYLES).map(([k, v]) => (
            <span key={k} className={`inline-flex items-center gap-2`}>
              <span className={`w-3 h-3 ${v.bg} inline-block`} />
              <span className="text-black/70 font-bold">{v.label}</span>
            </span>
          ))}
        </div>

        {/* Month grids */}
        <div className="grid grid-cols-1 gap-8">
          <MonthGrid year={2026} month={6} />
          <MonthGrid year={2026} month={7} />
        </div>

        {/* Event lists — collapsible */}
        <div className="mt-14 space-y-6">
          <EventSection
            testid="upcoming-events"
            title="Upcoming Events"
            subtitle="In chronological order. Tap for the full location on your phone map."
            open={upcomingOpen}
            onToggle={() => setUpcomingOpen((v) => !v)}
            events={upcoming}
            emptyText="No upcoming events yet. Check back weekly for updates."
          />
          <EventSection
            testid="past-events"
            title="Past Events"
            subtitle="Everything the Titans have already crossed off. Newest first."
            open={pastOpen}
            onToggle={() => setPastOpen((v) => !v)}
            events={past}
            emptyText="No past events yet — the season is just getting started."
          />
        </div>

        {/* CTA */}
        <div className="mt-14 bg-[#F5EFE0] border border-black/15 p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="font-display text-xs uppercase tracking-[0.3em] text-[#2D5F3F] font-bold">Not on the roster yet?</div>
            <h3 className="mt-2 font-display text-2xl md:text-3xl font-black uppercase leading-tight">Register before first practice</h3>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/register"
              data-testid="calendar-cta-football"
              className="inline-flex items-center gap-2 bg-[#2D5F3F] hover:bg-[#1B4332] text-[#FDFBF7] font-display uppercase tracking-wider font-extrabold px-6 py-3 transition-colors"
            >
              Football Registration
            </Link>
            <Link
              to="/cheer"
              data-testid="calendar-cta-cheer"
              className="inline-flex items-center gap-2 border-2 border-[#2D5F3F] text-[#2D5F3F] hover:bg-[#2D5F3F] hover:text-[#FDFBF7] font-display uppercase tracking-wider font-bold px-6 py-3 transition-colors"
            >
              Cheer Registration
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function EventCard({ e, dim = false }) {
  const style = TYPE_STYLES[e.type] || TYPE_STYLES.practice;
  const [yy, mm, dd] = e.iso.split("-").map(Number);
  const dt = new Date(yy, mm - 1, dd);
  const dow = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][dt.getDay()];
  const mapUrl = e.location ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(e.location)}` : null;
  return (
    <article
      data-testid={`event-${e.iso}`}
      className={`bg-white border border-black/15 p-5 flex items-start gap-4 hover:border-[#2D5F3F] transition-colors ${dim ? "opacity-70" : ""}`}
    >
      <div className={`shrink-0 w-16 text-center ${style.bg} ${style.text} py-2`}>
        <div className="font-display text-[10px] uppercase tracking-[0.2em] font-bold opacity-90">{dow}</div>
        <div className="font-display text-2xl font-black leading-none mt-1">{dd}</div>
        <div className="font-display text-[10px] uppercase tracking-[0.2em] font-bold opacity-90 mt-1">{MONTH_NAMES[mm - 1].slice(0, 3)}</div>
      </div>
      <div className="flex-1 min-w-0">
        <div className={`inline-block ${style.bg} ${style.text} px-2 py-0.5 font-display uppercase text-[10px] tracking-[0.2em] font-extrabold mb-2`}>
          {style.label}
        </div>
        <h3 className="font-display text-lg font-black uppercase text-[#0A0A0A] leading-tight">{e.title}</h3>
        {e.time && (
          <div className="mt-1.5 flex items-center gap-1.5 text-sm text-black/70">
            <Clock className="w-3.5 h-3.5" /> {e.time}
          </div>
        )}
        {e.location && (
          mapUrl ? (
            <a href={mapUrl} target="_blank" rel="noreferrer" className="mt-1 flex items-start gap-1.5 text-sm text-[#2D5F3F] hover:underline">
              <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" /> <span>{e.location}</span>
            </a>
          ) : (
            <div className="mt-1 flex items-start gap-1.5 text-sm text-black/70">
              <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" /> <span>{e.location}</span>
            </div>
          )
        )}
        {e.note && <p className="mt-2 text-sm text-black/60 italic">{e.note}</p>}
      </div>
    </article>
  );
}

function EventSection({ testid, title, subtitle, open, onToggle, events, emptyText }) {
  const isPast = testid === "past-events";
  return (
    <section data-testid={testid} className="bg-white border border-black/15">
      <button
        type="button"
        onClick={onToggle}
        data-testid={`${testid}-toggle`}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 md:px-6 md:py-5 hover:bg-[#F5EFE0] transition-colors text-left"
      >
        <div className="min-w-0">
          <h2 className="font-display text-2xl md:text-3xl font-black uppercase text-[#0A0A0A] leading-tight">{title}</h2>
          <p className="text-black/60 text-sm mt-0.5">{subtitle}</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="hidden sm:inline-flex items-center justify-center bg-[#2D5F3F] text-[#FDFBF7] font-display text-xs uppercase tracking-widest font-extrabold px-2.5 py-1">
            {events.length}
          </span>
          <ChevronDown className={`w-6 h-6 text-[#2D5F3F] transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={2.5} />
        </div>
      </button>
      {open && (
        <div className="px-5 pb-6 md:px-6 md:pb-8 border-t border-black/10 pt-6">
          {events.length === 0 ? (
            <p className="text-black/60 italic">{emptyText}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {events.map((e, i) => (
                <EventCard key={`${e.iso}-${i}`} e={e} dim={isPast} />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

